import React from 'react';
import { School } from '../../types/School';
import { TableColumn } from '../../utils/tableUtils';

// Status badge component for schools
const StatusBadge: React.FC<{ status: string; type: string }> = ({ status, type }) => {
  const getStatusColor = (status: string, type: string) => {
    if (type === 'active') {
      return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status, type)}`}>
      {status}
    </span>
  );
};

// Define school table columns
export const schoolColumns: TableColumn<School>[] = [
  {
    id: 'name',
    label: 'Name',
    accessor: (school) => school.name,
    sortable: true,
  },
  {
    id: 'displayName',
    label: 'Display Name',
    accessor: (school) => school.displayName || '-',
    sortable: true,
  },
  {
    id: 'address',
    label: 'Address',
    accessor: (school) => {
      if (!school.address) return '-';
      const { street, city, state, zipCode } = school.address;
      return `${street}, ${city}, ${state} ${zipCode}`;
    },
    sortable: false,
  },
  {
    id: 'phone',
    label: 'Phone',
    accessor: (school) => school.phone || '-',
    sortable: true,
  },
  {
    id: 'email',
    label: 'Email',
    accessor: (school) => school.email || '-',
    sortable: true,
  },
  {
    id: 'website',
    label: 'Website',
    accessor: (school) => school.website ? (
      <a
        href={school.website}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        Visit Site
      </a>
    ) : '-',
    sortable: false,
  },
  {
    id: 'isActive',
    label: 'Status',
    accessor: (school) => (
      <StatusBadge
        status={school.isActive ? 'Active' : 'Inactive'}
        type="active"
      />
    ),
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: () => null, // Actions are handled by the AdminTable component
    sortable: false,
  },
];

// School table configuration - Updated to work with AdminTable component
export const schoolTableConfig = {
  entityName: 'School',
  apiEndpoint: '/api/admin/schools', // Will create a custom endpoint that returns array
  columns: schoolColumns,
  searchFields: ['name', 'displayName', 'email'] as (keyof School)[],
  filterOptions: {
    isActive: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' }
    ],
    state: [] as { value: string; label: string }[] // Will be populated dynamically
  },
  exportFileName: 'schools',
  defaultVisibleColumns: {
    name: true,
    displayName: true,
    address: true,
    phone: true,
    email: true,
    website: true,
    isActive: true,
    actions: true
  },
  defaultSort: { field: 'name' as keyof School, direction: 'asc' as const },
  itemsPerPage: 25
};
